import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Input,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { Sparkles } from "lucide-react";
import React, { useState } from "react";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { LAO_MONTH_NAMES } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import {
  monthlyReportFormSamples,
  type MonthlyReportFormSample,
} from "./monthlyReportFormSamples";

type DepartmentItem = { id: string; name: string };

const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

const MonthlyReportFormSchema = z
  .object({
    periodYear: z.coerce.number().int().min(2000, "ກະລຸນາເລືອກປີ").max(2100),
    periodMonth: z.coerce.number().int().min(1, "ກະລຸນາເລືອກເດືອນ").max(12),
    departmentId: z.string().optional(),
    title: z.string().optional(),
    fileKey: z.string().optional(),
    documentUrl: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.fileKey && data.fileKey.trim()) ||
      (data.documentUrl && data.documentUrl.trim()),
    { message: "ຕ້ອງມີໄຟລ໌ອັບໂຫຼດ ຫຼື ລິ້ງ URL", path: ["fileKey"] },
  )
  .refine(
    (data) =>
      !data.documentUrl ||
      data.documentUrl.trim() === "" ||
      /^https?:\/\/.+/.test(data.documentUrl),
    { message: "ລິ້ງຕ້ອງເປັນ URL ທີ່ຖືກຕ້ອງ", path: ["documentUrl"] },
  );

export type MonthlyReportFormValues = z.infer<typeof MonthlyReportFormSchema>;

export type MonthlyReportFormRef = {
  submit: () => void;
};

type MonthlyReportFormProps = {
  defaultValues: MonthlyReportFormValues;
  onSubmit: (values: MonthlyReportFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: "create" | "edit";
  formRef?: React.RefObject<MonthlyReportFormRef | null>;
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
const MONTH_OPTIONS = LAO_MONTH_NAMES.map((name, i) => ({
  value: String(i + 1),
  label: name,
}));

export function MonthlyReportForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  formRef,
}: MonthlyReportFormProps) {
  const methods = RHF.useForm<MonthlyReportFormValues>({
    defaultValues,
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference with z.coerce
    resolver: zodResolver(MonthlyReportFormSchema) as any,
  });

  React.useImperativeHandle(
    formRef,
    () => ({
      submit: () => methods.handleSubmit(onSubmit)(),
    }),
    [methods, onSubmit],
  );

  const [sampleIndex, setSampleIndex] = useState(0);

  const handleAutoFill = () => {
    const sample: MonthlyReportFormSample | undefined =
      monthlyReportFormSamples[sampleIndex];
    if (sample) {
      methods.setValue("periodYear", sample.periodYear);
      methods.setValue("periodMonth", sample.periodMonth);
      methods.setValue("title", sample.title);
      setSampleIndex((prev) => (prev + 1) % monthlyReportFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາອັບໂຫຼດໄຟລ໌ ຫຼື ໃສ່ລິ້ງ URL.");
    }
  };

  return (
    <FormRoot<MonthlyReportFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="flex flex-col gap-6"
    >
      <Card>
        <CardHeader>
          <CardTitle>ຂໍ້ມູນລາຍງານປະຈໍາເດືອນ</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field name="periodYear" label="ປີ" requiredMark>
              <FormInput
                name="periodYear"
                type="number"
                placeholder="ປີ"
                min={2000}
                max={2100}
                aria-label="ປີ"
              />
            </Field>
            <FormSelect
              name="periodMonth"
              label="ເດືອນ"
              requiredMark
              placeholder="ເລືອກເດືອນ"
              options={MONTH_OPTIONS}
              aria-label="ເດືອນ"
            />
          </div>

          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ພະແນກ"
            queryKey={["departments", "monthly-report-form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
            className="max-w-md"
            clearable
          />

          <Field name="title" label="ຊື່ລາຍງານ / ຄຳອະທິບາຍ">
            <FormTextarea
              name="title"
              placeholder="ຊື່ ຫຼື ຄຳອະທິບາຍສັ້ນໆ (ບໍ່ບັງຄັບ)"
              rows={2}
            />
          </Field>

          <div className="space-y-4">
            <Field
              name="fileKey"
              label="ເອກະສານ"
              hint="ອັບໂຫຼດໄຟລ໌ PDF, Word ຫຼື ໃສ່ລິ້ງ URL"
              requiredMark
            >
              <div className="flex flex-col gap-4">
                {methods.watch("fileKey") && (
                  <p className="text-muted-foreground text-xs">
                    ໄຟລ໌ປັດຈຸບັນ:{" "}
                    <code className="rounded bg-muted px-1">
                      {methods.watch("fileKey")}
                    </code>{" "}
                    (ອັບໂຫຼດໃໝ່ເພື່ອທົດແທນ)
                  </p>
                )}
                <PresignedFileUpload
                  keyPrefix="uploads/monthly-reports"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onSuccess={(key) => {
                    methods.setValue("fileKey", key);
                    methods.setValue("documentUrl", "");
                    methods.clearErrors("fileKey");
                  }}
                  onError={(e) => {
                    toast.error(e.message);
                  }}
                  buttonLabel="ອັບໂຫຼດໄຟລ໌"
                  hint="ຮອງຮັບ PDF, Word, Excel"
                />
                <div>
                  <span className="text-muted-foreground text-sm">
                    ຫຼື ໃສ່ລິ້ງ URL:
                  </span>
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={methods.watch("documentUrl") ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      methods.setValue("documentUrl", v);
                      if (v) methods.setValue("fileKey", "");
                    }}
                    className="mt-1"
                  />
                </div>
                {(methods.formState.errors.fileKey ||
                  methods.formState.errors.documentUrl) && (
                  <p className="text-destructive text-sm">
                    {methods.formState.errors.fileKey?.message ??
                      methods.formState.errors.documentUrl?.message}
                  </p>
                )}
              </div>
            </Field>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          {isDev &&
            mode === "create" &&
            monthlyReportFormSamples.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAutoFill}
                disabled={isSubmitting}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Auto Fill (Sample {sampleIndex + 1}/
                {monthlyReportFormSamples.length})
              </Button>
            )}
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {mode === "create" ? "ບັນທຶກ" : "ບັນທຶກການປ່ຽນແປງ"}
        </Button>
      </div>
    </FormRoot>
  );
}
